import { Headphones, ChevronDown, Copy } from '@/shared/ui/icons';
import { Button } from '../../shared/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../shared/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../shared/ui/dropdown-menu';
import { toast } from 'sonner';
import { useOptionalCall } from './CallContext';
import { useOptionalHuddle } from './HuddleContext';
import { buildHuddleLink } from './huddleLinks';
import { huddleConversationName, supportsBrowserHuddles, type HuddleConversation } from './huddleConversation';

export function HuddleButton({ channel, size = 32, color }: { channel: HuddleConversation; size?: number; color?: string }) {
  const huddle = useOptionalHuddle();
  const call = useOptionalCall();
  if (!huddle || !supportsBrowserHuddles()) return null;
  const active = Boolean(huddle.activeChatId);
  const current = active && huddle.activeChatId === channel.id;
  const busy = Boolean(call && call.status !== 'idle');
  const name = huddleConversationName(channel.name, channel.kind);
  const preposition = channel.kind === 'direct' ? 'with' : 'in';
  const label = current ? `Open huddle ${preposition} ${name}` : active ? 'Return to active huddle' : `Start huddle ${preposition} ${name}`;
  const open = () => huddle.openHuddle(channel);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(buildHuddleLink(channel.id));
      toast.success('Huddle link copied');
    } catch {
      toast.error('Could not copy the huddle link');
    }
  };

  return (
    <div role="group" aria-label="Huddle" style={color ? { color } : undefined} className={`flex shrink-0 items-center rounded-md ${current ? 'bg-primary-ghost text-primary' : 'text-muted-foreground'}`}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button type="button" variant="ghost" size="icon" style={{ width: size, height: size }} className="rounded-md hover:text-foreground" aria-label={label} disabled={busy} onClick={open}>
            <Headphones className="size-[18px]" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{busy ? 'Finish your call before joining a huddle' : label}</TooltipContent>
      </Tooltip>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon" style={{ width: 20, height: size }} className="rounded-md hover:text-foreground" aria-label="Huddle options">
                <ChevronDown className="size-3" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Huddle options</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end">
          <DropdownMenuItem disabled={busy} onSelect={open}><Headphones />{active ? 'Return to huddle' : 'Start huddle'}</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => { void copyLink(); }}><Copy />Copy huddle link</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
